import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Send, FileDown, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface LeadFormProps {
  calculationData: any;
  subsidyResult: any;
}

export const LeadForm = ({ calculationData, subsidyResult }: LeadFormProps) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    consentMarketing: false,
    wantsPdf: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast({
        title: "Vereiste velden",
        description: "Vul minimaal uw naam en e-mailadres in.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Save submission to database
      const submissionData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        postcode: calculationData.postcode || null,
        measure_category: calculationData.category,
        measure_type: calculationData.type,
        measure_value: calculationData.value,
        housing_type: calculationData.housingType || null,
        build_year: calculationData.buildYear ? parseInt(calculationData.buildYear) : null,
        biobased: calculationData.biobased || false,
        energy_label: calculationData.energyLabel || null,
        estimated_subsidy: subsidyResult.totalSubsidy,
        calculation_details: {
          breakdown: subsidyResult.breakdown,
          warnings: subsidyResult.warnings,
          timestamp: new Date().toISOString()
        },
        consent_marketing: formData.consentMarketing,
        pdf_generated: formData.wantsPdf
      };

      const { error } = await supabase
        .from('isde_submissions')
        .insert([submissionData]);

      if (error) throw error;

      // TODO: Send email with calculation results (via Edge Function)
      // TODO: Generate PDF if requested

      setIsSubmitted(true);
      toast({
        title: "Aanvraag verzonden!",
        description: "We nemen zo snel mogelijk contact met u op.",
      });

    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Er ging iets mis",
        description: "Probeer het later opnieuw of neem direct contact op.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="text-center py-6">
            <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Bedankt voor uw aanvraag!</h3>
            <p className="text-muted-foreground mb-4">
              We hebben uw gegevens ontvangen en nemen binnen 24 uur contact met u op 
              voor een persoonlijk advies en offerte.
            </p>
            {formData.wantsPdf && (
              <p className="text-sm text-muted-foreground">
                De PDF met uw berekening wordt naar uw e-mailadres gestuurd.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="h-5 w-5" />
          Ontvang persoonlijk advies
        </CardTitle>
        <CardDescription>
          Krijg een gratis offerte en maatwerk advies voor uw situatie
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Naam *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Telefoonnummer</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="Voor sneller contact (optioneel)"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Aanvullende vragen of opmerkingen</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Bijv. gewenste uitvoerdatum, speciale wensen, vragen over subsidies..."
              rows={3}
            />
          </div>

          {/* Options */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="wantsPdf"
                checked={formData.wantsPdf}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, wantsPdf: checked as boolean }))}
              />
              <Label htmlFor="wantsPdf" className="flex items-center gap-2">
                <FileDown className="h-4 w-4" />
                PDF met berekening en stappenplan per e-mail ontvangen
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="consentMarketing"
                checked={formData.consentMarketing}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consentMarketing: checked as boolean }))}
              />
              <Label htmlFor="consentMarketing" className="text-sm">
                Ik wil graag tips en updates over verduurzaming en subsidies ontvangen
              </Label>
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              "Bezig met verzenden..."
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Vraag gratis advies aan
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            Door het versturen van dit formulier gaat u akkoord met onze{' '}
            <a href="/privacy" className="underline">privacyverklaring</a>.
            We nemen binnen 24 uur contact met u op.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};